<script>
	// ProductListTile
	// A tile for a single product intended to be displayed in list mode.

	// Setup ---
	// Use the values below to modify how the tile appears
	const setup = {
		tileHeight: '8rem', // Height of the full tab, fixed for alignment purposes
		productNameLines: 2 // Number of lines product name is allowed to wrap before being trimmed with ellipses
	};

	export const Setup = setup;
</script>

<script setup>
	// Components ----
	import LimitMessages from './LimitMessages';

	// Imports ----
	import { computed, reactive, defineProps } from 'vue';
	import { AddToCartForm } from '@dd-nucleus/nucleus-vue';
	import { store } from '@/Store';

	// Props ----
	const props = defineProps({
		// The product whose information to show.
		// API model: DD.Nucleus.Storefront.Api.ProductTileModel
		product: {
			type: Object,
			required: true
		}
	});

	// State ----
	const product = reactive(props.product);
	const form = reactive(new AddToCartForm(product));

	// Computed ----
	const showInStockQuantity = computed(() => {
		return product.limits == null || product.limits.length == 0;
	});

	const quantityLabel = computed(() => {
		return store.config.store?.unitOfMeasures.find(unit => unit.id?.toLowerCase() === product.unitOfMeasure?.toLowerCase())?.text ?? null;
	});

	// Handlers ----
</script>

<template>
	<div class="product-list-tile" :style="{ height: setup.tileHeight }">
		<div class="inner-row d-flex flex-row">
			<div class="left">
				<ProductThumbnail :product="product" :height="setup.tileHeight" width="200px" no-image-available-url="/images/no-image.png" />
			</div>
			<div class="middle flex-fill">
				<!-- Stretchy middle part-->

				<div class="tags mb-2">
					<ProductTags :product="product" />
				</div>

				<!-- Product name and item number -->
				<router-link class="title-link" :to="`/product/${product.id}`">
					<div class="name" v-snip="{ lines: setup.productNameLines }" :title="product.name">
						{{ product.name }}
					</div>
				</router-link>
				<div class="item-number">
					<router-link class="title-link" :to="`/product/${product.id}`">{{ product.itemNumber }}</router-link>
				</div>

				<LimitMessages :product="product" class="mt-2" />

				<span v-if="showInStockQuantity" class="filter-messages">Qty in stock: {{ product.quantityAvailable }} </span>

				<div class="favorite">
					<ProductFavoriteIcon :product="product" label="Add as Favorite" />
				</div>
			</div>
			<!-- Fixed right panel -->
			<div class="right d-flex flex-column" :style="{ height: setup.tileHeight }">
				<MessageList :messages="product.messages" :add-box="false" />
				<p v-if="product.quantityPerUnit > 0">Quantity: {{ product.quantityPerUnit }} {{ product.quantityPerUnit > 1 ? 'units' : 'unit' }} per {{ quantityLabel }}</p>
				<FormContainer :form="form" v-if="product.canAddToCart">
					<div class="flex-fill">
						<ProductAddToCartQty :product="product" />
						<FormMessages field-name="quantity" :add-box="false" class="mt-2" />
						<FieldMessages field-name="quantity" class="mt-2" />
					</div>
					<div class="d-grid mt-3">
						<button class="btn btn-outline-color-1" @click="form.submit()" :disabled="form.status.isInvalid">ADD TO CART</button>
					</div>
				</FormContainer>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
	.product-list-tile {
		p {
			font-size: 12px;
		}
		.inner-row {
			border-bottom: 1px solid $gray-20;
			padding-bottom: 1rem;
		}

		.name {
			font-size: 1rem;
			line-height: 1.1rem;
		}

		a.title-link,
		a.title-link:visited,
		a.title-link:hover {
			color: var(--bs-body-color);
			text-decoration: none;
		}

		.item-number a.title-link {
			font-size: 0.7rem;
			color: $gray-50;
		}

		.n-product-add-to-cart-qty .text-box {
			border-color: #dddddd;
		}

		.btn[disabled] {
			opacity: 0.2;
		}

		.middle {
			padding-left: 1rem;
		}

		.favorite {
			margin-top: 1rem;
		}

		.right {
			width: 14rem;
		}
	}
</style>
